import { createEntityAdapter } from "@reduxjs/toolkit";
import { apiSlice } from "../api/apiSlice";

const csvAdapter = createEntityAdapter();

const initialState = csvAdapter.getInitialState();

export const csvAPISlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Export CSV endpoints
    exportLoadShiftingCSV: builder.query({
      query: () => ({
        url: "/dsm/export-csv/load-shifting/",
        responseHandler: async (response) => {
          if (!response.ok) throw new Error(`Export failed (${response.status})`);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `load_shifting_schedules_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
      }),
    }),

    exportPeakShavingCSV: builder.query({
      query: () => ({
        url: "/dsm/export-csv/peak-shaving/",
        responseHandler: async (response) => {
          if (!response.ok) throw new Error(`Export failed (${response.status})`);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `peak_shaving_schedules_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
      }),
    }),

    exportPublicHolidaysCSV: builder.query({
      query: () => ({
        url: "/dsm/export-csv/public-holidays/",
        responseHandler: async (response) => {
          if (!response.ok) throw new Error(`Export failed (${response.status})`);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `public_holidays_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
      }),
    }),

    exportTOUDataCSV: builder.query({
      query: () => ({
        url: "/dsm/export-csv/tou-data/",
        responseHandler: async (response) => {
          if (!response.ok) throw new Error(`Export failed (${response.status})`);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `tou_data_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
      }),
    }),

    exportTariffDataCSV: builder.query({
      query: () => ({
        url: "/dsm/export-csv/tariff-data/",
        responseHandler: async (response) => {
          if (!response.ok) throw new Error(`Export failed (${response.status})`);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `tariff_data_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
      }),
    }),

    // Validation endpoints
    validateLoadShiftingCSV: builder.mutation({
      query: (formData) => ({
        url: "/dsm/validate-csv/load-shifting/",
        method: "POST",
        body: formData,
      }),
    }),

    validatePeakShavingCSV: builder.mutation({
      query: (formData) => ({
        url: "/dsm/validate-csv/peak-shaving/",
        method: "POST",
        body: formData,
      }),
    }),

    validatePublicHolidaysCSV: builder.mutation({
      query: (formData) => ({
        url: "/dsm/validate-csv/public-holidays/",
        method: "POST",
        body: formData,
      }),
    }),

    validateTOUDataCSV: builder.mutation({
      query: (formData) => ({
        url: "/dsm/validate-csv/tou-data/",
        method: "POST",
        body: formData,
      }),
    }),

    validateTariffDataCSV: builder.mutation({
      query: (formData) => ({
        url: "/dsm/validate-csv/tariff-data/",
        method: "POST",
        body: formData,
      }),
    }),

    // Import endpoints
    importLoadShiftingCSV: builder.mutation({
      query: (data) => ({
        url: "/dsm/import-csv/load-shifting/",
        method: "POST",
        body: { data },
      }),
      invalidatesTags: ["schedule_ls"],
    }),

    importPeakShavingCSV: builder.mutation({
      query: (data) => ({
        url: "/dsm/import-csv/peak-shaving/",
        method: "POST",
        body: { data },
      }),
      invalidatesTags: ["schedule_ps"],
    }),

    importPublicHolidaysCSV: builder.mutation({
      query: (data) => ({
        url: "/dsm/import-csv/public-holidays/",
        method: "POST",
        body: { data },
      }),
      invalidatesTags: ["public_holidays"],
    }),

    importTOUDataCSV: builder.mutation({
      query: (data) => ({
        url: "/dsm/import-csv/tou-data/",
        method: "POST",
        body: { data },
      }),
      invalidatesTags: ["tou_data"],
    }),

    importTariffDataCSV: builder.mutation({
      query: (data) => ({
        url: "/dsm/import-csv/tariff-data/",
        method: "POST",
        body: { data },
      }),
      invalidatesTags: ["tariff_data"],
    }),
  }),
});

export const {
  // Export hooks
  useLazyExportLoadShiftingCSVQuery,
  useLazyExportPeakShavingCSVQuery,
  useLazyExportPublicHolidaysCSVQuery,
  useLazyExportTOUDataCSVQuery,
  useLazyExportTariffDataCSVQuery,
  
  // Validation hooks
  useValidateLoadShiftingCSVMutation,
  useValidatePeakShavingCSVMutation,
  useValidatePublicHolidaysCSVMutation,
  useValidateTOUDataCSVMutation,
  useValidateTariffDataCSVMutation,
  
  // Import hooks
  useImportLoadShiftingCSVMutation,
  useImportPeakShavingCSVMutation,
  useImportPublicHolidaysCSVMutation,
  useImportTOUDataCSVMutation,
  useImportTariffDataCSVMutation,
} = csvAPISlice;